// Living Interface — trusted Surface Renderer (D-237 / PRIN-026).
//
// Renders a VALIDATED SurfaceSpec (fetched from the backend, which has already run the full validator
// pipeline) using a fixed registry of trusted components. The model never reaches this code: there is
// no eval, no new Function, no dangerouslySetInnerHTML, no raw HTML — every text value is rendered as
// a plain string, and every component is one of the registered few below. Unknown block types (which
// validation should already have rejected) render an explicit error, never anything executable.
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './surface.css';

const API = process.env.REACT_APP_API_URL || '/api';

function scrollToSection(target) {
  const id = String(target || '').replace(/^section:/, '');
  const el = document.getElementById(`li-sec-${id}`);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// Declarative interactions. Phase 1 has no capability broker wired, so capability actions render as
// inert (disabled) buttons; only local navigation acts. The renderer NEVER interprets arbitrary code.
function Actions({ interactions }) {
  if (!interactions || !interactions.length) return null;
  return (
    <div className="li-actions">
      {interactions.map((it, i) => it.action === 'navigate_local' ? (
        <button key={i} onClick={() => scrollToSection(it.target)}>{it.label}</button>
      ) : (
        <button key={i} disabled title="Read capability — wired in a later phase">{it.label}</button>
      ))}
    </div>
  );
}

function Refs({ refs }) {
  if (!refs || !refs.length) return null;
  return <div className="li-ref">{refs.join(' · ')}</div>;
}

function Block({ block }) {
  const b = block;
  switch (b.type) {
    case 'highlight':
      return (
        <div className={`li-card imp-${b.importance || 'normal'}`}>
          <p className="li-card-title">{b.title}</p>
          {b.content && <p className="li-card-body">{b.content}</p>}
          <Refs refs={b.evidenceRefs} />
          <Actions interactions={b.interactions} />
        </div>
      );
    case 'narrative':
      return (
        <div className="li-card">
          {b.title && <p className="li-card-title">{b.title}</p>}
          <p className="li-card-body">{b.content}</p>
          <Refs refs={b.evidenceRefs} />
          <Actions interactions={b.interactions} />
        </div>
      );
    case 'metric':
      return (
        <div className="li-card li-metric">
          <span className="li-metric-label">{b.label}</span>
          <span className="li-metric-value">{b.value}</span>
          {b.delta && <span className={`li-delta ${b.trend || 'flat'}`}>{b.delta}</span>}
          <Refs refs={b.evidenceRefs} />
        </div>
      );
    case 'table':
      return (
        <div className="li-card">
          {b.title && <p className="li-card-title">{b.title}</p>}
          <table className="li-table">
            <thead><tr>{(b.columns || []).map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
            <tbody>
              {(b.rows || []).map((row, r) => (
                <tr key={r}>{(row || []).map((cell, c) => <td key={c}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
          <Refs refs={b.evidenceRefs} />
        </div>
      );
    case 'timeline':
      return (
        <div className="li-card">
          {b.title && <p className="li-card-title">{b.title}</p>}
          <ul className="li-timeline">
            {(b.events || []).map((e, i) => (
              <li key={i}><span className="at">{e.at || ''}</span><span>{e.label}</span></li>
            ))}
          </ul>
          <Refs refs={b.evidenceRefs} />
        </div>
      );
    case 'decision_card':
      return (
        <div className="li-card">
          <span className={`li-pill ${b.status}`}>{b.status}</span>
          <p className="li-card-title">{b.title}</p>
          {b.question && <p className="li-card-body">{b.question}</p>}
          {b.options && b.options.length > 0 && (
            <ul>{b.options.map((o, i) => <li key={i}>{o}</li>)}</ul>
          )}
          <Refs refs={b.evidenceRefs} />
          <Actions interactions={b.interactions} />
        </div>
      );
    case 'evidence_list':
      return (
        <div className="li-card">
          {b.title && <p className="li-card-title">{b.title}</p>}
          <ul className="li-evidence">
            {(b.items || []).map((it, i) => (
              <li key={i}>{it.grade && <span className="grade">[{it.grade}] </span>}{it.label} <span className="li-ref">{it.ref}</span></li>
            ))}
          </ul>
          <Actions interactions={b.interactions} />
        </div>
      );
    case 'line_chart':
      // Phase 1: no chart library and datasets are resolved server-side in Phase 2 — show an honest stub.
      return (
        <div className="li-card">
          {b.title && <p className="li-card-title">{b.title}</p>}
          <div className="li-chart-stub">chart ({b.datasetRef}) — data wired in a later phase</div>
          <Refs refs={b.evidenceRefs} />
        </div>
      );
    default:
      return <div className="li-unknown">Unsupported block: {String(b.type)}</div>;
  }
}

function Section({ section }) {
  const s = section;
  return (
    <section id={`li-sec-${s.id}`} className={`li-section pri-${s.priority || 'normal'}`}>
      {s.title && <h3 className="li-section-title">{s.title}</h3>}
      <div className={`li-blocks li-layout-${s.layout}`}>
        {(s.blocks || []).map((b, i) => <Block key={i} block={b} />)}
      </div>
    </section>
  );
}

// Per-module status chip: composing… / live · <model> / raw (deterministic, with the fallback reason).
function ModuleStatus({ m }) {
  const cls = m.composing ? 'composing' : m.source === 'composer' ? '' : 'fallback';
  const text = m.composing ? 'composing…'
    : m.source === 'composer' ? `live · ${m.model || 'model'}`
      : 'raw';  // deterministic render (no LLM) — the instant first paint or an LLM fallback
  return (
    <span className={`li-status ${cls}`}>
      {text}{m.diagnostic ? ` — ${m.diagnostic}` : ''}
    </span>
  );
}

// Progressive, priority-ordered briefing (D-237 Phase 3b): first paint every module DETERMINISTICALLY
// (instant, no LLM) in priority order, then upgrade each in place with its LLM composition as it returns.
// The user reads content immediately instead of waiting on one slow mega-compose.
// Diagnostics panel (D-241 Phase A): when the briefing is empty, fetch /surface/debug and show WHY on
// screen — signal counts, the most-recent notes in the KB, and knowledge.health — so facts are one refresh
// away, no DevTools cURL.
function DiagnosticsPanel({ d }) {
  if (!d) return <div className="li-status">running diagnostics…</div>;
  const c = d.signalCounts || {};
  const recent = d.recentNotes || [];
  return (
    <div className="li-module">
      <p className="li-card-title">Diagnostics — why it's empty</p>
      <p className="li-card-body">
        Signals assembled — interest: {c.interest ?? '?'}, kingdom: {c.kingdom ?? '?'}, genesis: {c.genesis ?? '?'}, platform: {c.platform ?? '?'}.
        {typeof c.interest === 'number' && c.interest > 0 ? ' Findings exist but none rendered (safety-validation).' : ''}
      </p>
      <p className="li-card-body"><b>Most-recent notes in the knowledge base:</b></p>
      <table className="li-table">
        <thead><tr><th>date</th><th>type/status</th><th>author</th><th>title</th></tr></thead>
        <tbody>{recent.length === 0 ? <tr><td colSpan={4}>none returned</td></tr>
          : recent.map((n, i) => <tr key={i}><td>{n.date || ''}</td><td>{n.type || ''}</td><td>{n.created_by || ''}</td><td>{n.title || ''}</td></tr>)}</tbody>
      </table>
      {d.knowledgeHealthError && <p className="li-card-body">knowledge.health: {d.knowledgeHealthError}</p>}
      <details><summary className="li-status">raw diagnostics</summary>
        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '.68rem', overflowX: 'auto' }}>{JSON.stringify(d, null, 2)}</pre>
      </details>
    </div>
  );
}

export default function SurfaceRenderer({ surfaceId = 'king.home' }) {
  const { getAccessTokenSilently } = useAuth0();
  const [modules, setModules] = useState(null);   // null = loading, [] = nothing to show
  const [error, setError] = useState(null);
  const [diag, setDiag] = useState(null);

  const authFetch = useCallback(async (path) => {
    const token = await getAccessTokenSilently();
    const r = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  }, [getAccessTokenSilently]);

  // Upgrade one module in place with its LLM composition (or its deterministic fallback + reason).
  const upgradeModule = useCallback(async (id) => {
    try {
      const data = await authFetch(`/surface/${surfaceId}/module/${id}`);
      if (!data.surface) return;  // empty module — keep the deterministic first paint
      setModules((ms) => ms && ms.map((m) => m.id === id
        ? { ...m, surface: data.surface, source: data.source, model: data.model, diagnostic: data.diagnostic, composing: false }
        : m));
    } catch (e) {
      setModules((ms) => ms && ms.map((m) => m.id === id
        ? { ...m, composing: false, diagnostic: String(e.message || e) } : m));
    }
  }, [authFetch, surfaceId]);

  const load = useCallback(async () => {
    setError(null); setModules(null); setDiag(null);
    try {
      const data = await authFetch(`/surface/${surfaceId}/modules`);   // instant deterministic first paint
      const enabled = !!data.composerEnabled;
      const base = (data.modules || []).map((m) => ({ ...m, composing: enabled }));
      setModules(base);
      if (enabled) base.forEach((m) => upgradeModule(m.id));  // fan out LLM upgrades; render keeps priority order
      if (base.length === 0) {  // nothing to brief -> pull the diagnostics so WHY is on screen (D-241 Phase A)
        authFetch(`/surface/debug`).then(setDiag).catch((e) => setDiag({ error: String(e.message || e) }));
      }
    } catch (e) {
      setError(String(e.message || e));
    }
  }, [authFetch, surfaceId, upgradeModule]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="li-error">Briefing unavailable ({error}). Use the other tabs for live detail.</div>;
  if (modules === null) return <div className="li-loading">Loading briefing…</div>;

  return (
    <div className="li-surface">
      <div className="li-surface-head">
        <h2 className="li-title">King's Briefing</h2>
        <p className="li-subtitle">By priority — urgent first; each module upgrades as it composes.</p>
        <span className="li-status">
          <button onClick={load} style={{ border: 'none', background: 'none', color: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}>refresh</button>
        </span>
      </div>
      {modules.length === 0 && <div className="li-error">Nothing to brief right now.</div>}
      {modules.length === 0 && <DiagnosticsPanel d={diag} />}
      {modules.map((m) => (
        <div key={m.id} className="li-module">
          <div className="li-module-head"><ModuleStatus m={m} /></div>
          {((m.surface && m.surface.sections) || []).map((s, i) => <Section key={s.id || i} section={s} />)}
        </div>
      ))}
    </div>
  );
}
